import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useSidebar } from '@context/SidebarContext'

import { isSidebarItemActive, stripLocale } from '@shared/components/Navigation/v2/Sidebar/utils'

import { MenuLink } from '../../MenuLink'
import { MenuSection, MenuVariant } from '../../menuSections/useMenuSections'

import styles from './SidebarItem.module.scss'

interface SidebarItemProps {
  item: MenuSection
}

export const SidebarItem: React.FC<SidebarItemProps> = ({ item }) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const {
    isExpanded,
    activeSubmenu,
    openSubmenu,
    closeSubmenu,
    requestSubmenuChange,
    cancelPendingSubmenuChange,
  } = useSidebar()

  const buttonRef = useRef<HTMLButtonElement>(null)

  const hasChildren = !!item.children?.length
  const isQuickAccessItem = item.variant === MenuVariant.ACTION
  const isWallets = item.id === 'wallets'
  const isTrade = item.id === 'trade-menu'
  const isAccountSwitch = item.id === 'account-switch'
  const isProductSwitch = item.id === 'product-switch'

  const current = stripLocale(pathname)

  const isActive = useMemo(() => {
    return isSidebarItemActive(current, item, hasChildren)
  }, [current, item, hasChildren])

  const isSubmenuActive = activeSubmenu === item.id

  useEffect(() => {
    if (!buttonRef.current) return

    const target =
      (isWallets && 'wallets.nav') ||
      (isTrade && 'navigation.trade') ||
      (isAccountSwitch && 'wallets.accountSwitch') ||
      (isProductSwitch && 'wallets.productSwitch')

    if (target) {
      buttonRef.current.setAttribute('data-tour-target', target)
    }
  }, [isWallets, isTrade, isAccountSwitch, isProductSwitch])

  const handleClick = useCallback(() => {
    if (item.onClick) {
      item.onClick()
      return
    }

    if (hasChildren) {
      if (isSubmenuActive) {
        closeSubmenu()
      } else {
        const top = buttonRef.current?.getBoundingClientRect().top ?? 0
        openSubmenu(item.id, top)
      }
      return
    }

    if (item.path) {
      navigate(item.path)
    }
  }, [item, hasChildren, isSubmenuActive, openSubmenu, closeSubmenu, navigate])

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent) => {
      const point = {
        x: event.clientX,
        y: event.clientY,
      }

      if (hasChildren) {
        const top = buttonRef.current?.getBoundingClientRect().top ?? 0

        requestSubmenuChange(
          {
            id: item.id,
            top,
          },
          point
        )

        return
      }

      requestSubmenuChange(null, point)
    },
    [hasChildren, item.id, requestSubmenuChange]
  )

  const handleMouseLeave = useCallback(() => {
    cancelPendingSubmenuChange()
  }, [cancelPendingSubmenuChange])

  return (
    <MenuLink
      section={item}
      isActive={isQuickAccessItem ? false : isActive}
      isExpanded={isSubmenuActive}
      isVisible={isExpanded}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      buttonRef={buttonRef}
      className={styles.sideBarItem}
      showChevron={hasChildren}
    />
  )
}
